import {
  GROCERY_CATEGORIES,
  type GroceryCategory,
  type ShoppingItemWriteInput,
  type ShoppingListItem,
} from '@recipes/contracts';
import {
  classifyGroceryCategory,
  normalizeUnit,
  parseIngredientLine,
  parseQuantity,
} from '@recipes/domain';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@recipes/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField } from '@/features/shared/form-field';
import { groceryCategoryLabel } from '@/features/shared/labels';

/** Quick parse of "2 kg Kartoffeln" into a write input, with grocery classification. */
export function parseShoppingLine(line: string): ShoppingItemWriteInput {
  const parsed = parseIngredientLine(line);
  return {
    name: parsed.name,
    quantity: parsed.quantity,
    quantityMax: parsed.quantityMax,
    quantityText: parsed.quantityText,
    unitCode: parsed.unitCode,
    unitText: parsed.unitText,
    note: parsed.note,
    groceryCategory: classifyGroceryCategory(parsed.name),
  };
}

export function ItemFormDialog({
  open,
  onOpenChange,
  item,
  onSubmit,
  saving,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: ShoppingListItem | null;
  onSubmit: (input: ShoppingItemWriteInput) => void;
  saving?: boolean;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('');
  const [note, setNote] = useState('');
  const [category, setCategory] = useState<GroceryCategory>('other');

  useEffect(() => {
    if (!open) return;
    setName(item?.name ?? '');
    setQuantity(item?.quantity ?? item?.quantityText ?? '');
    setUnit(item?.unitCode ?? item?.unitText ?? '');
    setNote(item?.note ?? '');
    setCategory(item?.groceryCategory ?? 'other');
  }, [open, item]);

  const submit = () => {
    const q = quantity.trim();
    const parsedQ = q ? parseQuantity(q) : null;
    const u = unit.trim();
    const catalog = u ? normalizeUnit(u) : undefined;
    onSubmit({
      name: name.trim(),
      quantity: parsedQ,
      quantityText: q && !parsedQ ? q : null,
      unitCode: catalog?.code ?? null,
      unitText: catalog ? null : u || null,
      note: note.trim() || null,
      groceryCategory: category,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{item ? t('shopping.item.edit') : t('shopping.item.add')}</DialogTitle>
        </DialogHeader>
        <form
          noValidate
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) submit();
          }}
        >
          <FormField label={t('shopping.item.name')} required>
            <Input value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label={t('shopping.item.quantity')} hint={t('shopping.item.quantityHint')}>
              <Input
                inputMode="decimal"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </FormField>
            <FormField label={t('shopping.item.unit')}>
              <Input value={unit} maxLength={40} onChange={(e) => setUnit(e.target.value)} />
            </FormField>
          </div>
          <FormField label={t('shopping.item.note')} optionalText={t('common.optional')}>
            <Input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
          </FormField>
          <div>
            <Label htmlFor="item-category">{t('shopping.item.category')}</Label>
            <Select value={category} onValueChange={(v) => setCategory(v as GroceryCategory)}>
              <SelectTrigger id="item-category" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GROCERY_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {groceryCategoryLabel(t, c)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={saving} disabled={!name.trim()}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
